import { useCallback, useEffect, useRef, useState, memo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveHeader } from "@/components/ui/responsive-header";
import { Button } from "@/components/ui/button";
import WelcomeStep from "../components/intake/WelcomeStep";
import MedicalIntakeStep from "../components/intake/MedicalIntakeStep";
import DynamicQuestionTree from "../components/intake/DynamicQuestionTree";
import PhotoCaptureStep from "../components/intake/PhotoCaptureStep";
import AdditionalPhotosStep from "../components/intake/AdditionalPhotosStep";
import ComprehensiveDentalAnalysis from "../components/intake/ComprehensiveDentalAnalysis";
import ReviewStep from "../components/intake/ReviewStep";
import OTPVerificationStep from "../components/intake/OTPVerificationStep";
import SuccessStep from "../components/intake/SuccessStep";
import SinglePhotoRetake from "../components/intake/SinglePhotoRetake";
import { patientIntakeApi, buildFormPayload } from "@/lib/patientIntakeApi";
import { useIOSKeyboard } from "@/hooks/use-ios-keyboard";

import {
  Camera,
  ClipboardCheck,
  UserCheck,
  FileText,
  CheckCircle,
  Brain,
  HelpCircle,
  ChevronLeft,
  Upload,
  Shield,
} from "lucide-react";

export interface IntakeData {
  // Personal info
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;

  // Location fields with HubSpot internal names
  city: string;
  state: string;
  country: string;

  // Medical questions
  preExistingConditions: string;
  currentMedications: string;
  allergies: string;
  majorSurgeries: string;
  dentalProcedureType: string;
  dentalDiscomfort: string;
  mobilityIssues: string;
  dietaryRestrictions: string;
  travelConcerns: string;
  smokingHabits: string;
  travelAlone: string;
  budgetExpectations: string;
  personalInterest: string;

  // Dynamic Questions - All possible dynamic question fields
  // Orthodontics
  orthodonticHistory?: string;
  orthodonticGoals?: string[];
  treatmentDuration?: string;
  appliancePreference?: string;

  // Implants
  missingTeethCount?: string;
  missingTeethDuration?: string;
  boneGraftHistory?: string;
  implantExpectations?: string[];

  // Cosmetic
  cosmeticGoals?: string[];
  previousCosmetic?: string;
  smileConfidence?: string;
  socialImpact?: string;

  // Restoration
  reconstructionReason?: string[];
  currentPainLevel?: string;
  functionIssues?: string[];
  treatmentPriority?: string;

  // Veneers
  veneerGoals?: string[];
  teethToTreat?: string;
  currentTeethCondition?: string;

  // Crowns
  crownReason?: string[];
  numberOfCrowns?: string;
  crownMaterial?: string;
  urgencyLevel?: string;

  // Whitening
  whiteningGoal?: string;
  stainingType?: string[];
  previousWhitening?: string;
  sensitivityConcern?: string;

  // General
  lastDentalVisit?: string;
  dentalConcerns?: string[];
  treatmentInterest?: string[];

  // Photos - Updated for 6-photo orthodontic capture
  photos: {
    upper_occlusal?: string;
    frontal_retracted_apart?: string;
    lower_occlusal?: string;
    right_buccal?: string;
    frontal_in_occlusion?: string;
    left_buccal?: string;
  };
  photoMediaMap?: Record<string, string>;

  // X-rays
  xrays?: string[];

  // AI Analysis
  aiDiagnosis?: any;
  estimatedCost?: {
    min: number;
    max: number;
    currency: string;
  };
  recommendedTreatments?: string[];

  // Comprehensive Analysis
  comprehensiveAnalysis?: any;
  finalDiagnosis?: any[];
  treatmentPlan?: any[];
  estimatedTotalCost?: {
    min: number;
    max: number;
  };

  // Optional Services
  selectedServices?: string[];
  additionalServicesCost?: number;

  // Tours and Services
  selectedExperience?: string;
  selectedTours?: string[];

  // Additional Photos
  additionalPhotos?: string[];
  additionalPhotoMediaIds?: string[];

  // Consent
  consentGiven: boolean;

  // Form metadata
  formUid?: string;
  referenceUid?: string;
}

const STEPS = [
  { id: "welcome", title: "Welcome", icon: UserCheck },
  { id: "medical", title: "Medical Info", icon: FileText },
  { id: "detailed", title: "Detailed Questions", icon: HelpCircle },
  { id: "photos", title: "Your Photos", icon: Camera },
  { id: "additional-photos", title: "Additional Photos", icon: Upload },
  { id: "analysis", title: "Full Analysis", icon: Brain },
  { id: "review", title: "Review & Submit", icon: ClipboardCheck },
  { id: "verification", title: "Email Verification", icon: Shield },
  { id: "success", title: "All Done!", icon: CheckCircle },
];

const MemoizedWelcomeStep = memo(WelcomeStep);
const MemoizedMedicalIntakeStep = memo(MedicalIntakeStep);
const MemoizedDynamicQuestionTree = memo(DynamicQuestionTree);
const MemoizedPhotoCaptureStep = memo(PhotoCaptureStep);
const MemoizedAdditionalPhotosStep = memo(AdditionalPhotosStep);
const MemoizedComprehensiveDentalAnalysis = memo(ComprehensiveDentalAnalysis);
const MemoizedReviewStep = memo(ReviewStep);
const MemoizedOTPVerificationStep = memo(OTPVerificationStep);
const MemoizedSuccessStep = memo(SuccessStep);

export default function Index() {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retakePhotoContext, setRetakePhotoContext] = useState<{
    photoId: string;
    mediaId?: string;
  } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Handle iOS keyboard behavior
  useIOSKeyboard();
  const [intakeData, setIntakeData] = useState<IntakeData>({
    firstName: searchParams.get("firstName") || "",
    lastName: searchParams.get("lastName") || "",
    email: searchParams.get("email") || "",
    phone: searchParams.get("phone") || "",
    dateOfBirth: "",
    city: "",
    state: "",
    country: "",
    preExistingConditions: "",
    currentMedications: "",
    allergies: "",
    majorSurgeries: "",
    dentalProcedureType: "",
    dentalDiscomfort: "",
    mobilityIssues: "",
    dietaryRestrictions: "",
    travelConcerns: "",
    smokingHabits: "",
    travelAlone: "",
    budgetExpectations: "",
    personalInterest: "",
    photos: {},
    additionalPhotos: [],
    consentGiven: false,
    formUid: searchParams.get("formUid") || undefined,
    referenceUid: searchParams.get("referenceUid") || undefined,
  });
  const intakeDataRef = useRef<IntakeData>(intakeData);
  const draftSyncTimeoutRef = useRef<number | null>(null);
  const draftSyncPromiseRef = useRef<Promise<void> | null>(null);
  const lastSyncedSignatureRef = useRef<string | null>(null);

  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth >= 768;
  });

  useEffect(() => {
    intakeDataRef.current = intakeData;
  }, [intakeData]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "intake_start", {
        event_category: "dental_intake",
        event_label: "intake_flow_started",
      });
    }
  }, []);

  useEffect(() => {
    // Only apply mobile-specific styles on mobile devices
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Store original styles
      const originalHtmlOverflow = document.documentElement.style.overflow;
      const originalBodyOverflow = document.body.style.overflow;

      // Apply mobile styles (iOS keyboard handling is done by the hook)
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";

      return () => {
        // Restore original styles
        document.documentElement.style.overflow = originalHtmlOverflow;
        document.body.style.overflow = originalBodyOverflow;
      };
    }
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant" as ScrollBehavior,
      });
    }
  }, [currentStep]);

  useEffect(() => {
    stepRefs.current[currentStep]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [currentStep]);

  const performDraftSync = useCallback(async () => {
    const initial = intakeDataRef.current;
    const initialFormUid = initial.formUid;
    if (!initialFormUid) {
      return;
    }

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Previous sync errors are already logged
      }
    }

    const latest = intakeDataRef.current;
    if (!latest.formUid) {
      return;
    }

    const payload = buildFormPayload(latest);
    const signature = JSON.stringify(payload);
    if (lastSyncedSignatureRef.current === signature) {
      return;
    }

    const syncPromise = (async () => {
      try {
        await patientIntakeApi.updateIntake(latest.formUid!, latest);
        lastSyncedSignatureRef.current = signature;
      } catch (error) {
        console.error("Failed to sync intake draft", error);
      }
    })();

    draftSyncPromiseRef.current = syncPromise;

    try {
      await syncPromise;
    } finally {
      if (draftSyncPromiseRef.current === syncPromise) {
        draftSyncPromiseRef.current = null;
      }
    }
  }, []);

  const scheduleDraftSync = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!intakeDataRef.current.formUid) {
      return;
    }

    if (draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
    }

    draftSyncTimeoutRef.current = window.setTimeout(() => {
      draftSyncTimeoutRef.current = null;
      void performDraftSync();
    }, 800);
  }, [performDraftSync]);

  const flushDraftSync = useCallback(async () => {
    if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }

    await performDraftSync();

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Error already logged during performDraftSync
      }
    }
  }, [performDraftSync]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
      void flushDraftSync();
    };
  }, [flushDraftSync]);

  const updateIntakeData = useCallback(
    (data: Partial<IntakeData>) => {
      setIntakeData((prev) => {
        const next = { ...prev, ...data };
        intakeDataRef.current = next;
        if (next.formUid) {
          scheduleDraftSync();
        }
        return next;
      });
    },
    [setIntakeData, scheduleDraftSync],
  );

  const ensureDraftForm = useCallback(
    async (identityUpdates: Partial<IntakeData>) => {
      const sanitizedUpdates: Partial<IntakeData> = {};

      if (identityUpdates.firstName !== undefined) {
        sanitizedUpdates.firstName = identityUpdates.firstName.trim();
      }
      if (identityUpdates.lastName !== undefined) {
        sanitizedUpdates.lastName = identityUpdates.lastName.trim();
      }
      if (identityUpdates.email !== undefined) {
        sanitizedUpdates.email = identityUpdates.email.trim().toLowerCase();
      }
      if (identityUpdates.phone !== undefined) {
        sanitizedUpdates.phone = identityUpdates.phone.trim();
      }
      if (identityUpdates.dateOfBirth !== undefined) {
        sanitizedUpdates.dateOfBirth = identityUpdates.dateOfBirth;
      }
      if (identityUpdates.city !== undefined) {
        sanitizedUpdates.city = identityUpdates.city.trim();
      }
      if (identityUpdates.state !== undefined) {
        sanitizedUpdates.state = identityUpdates.state.trim();
      }
      if (identityUpdates.country !== undefined) {
        sanitizedUpdates.country = identityUpdates.country.trim();
      }

      updateIntakeData(sanitizedUpdates);

      const current = intakeDataRef.current;
      const existingFormUid = current.formUid;

      if (existingFormUid) {
        return;
      }

      const email = (current.email || "").trim().toLowerCase();
      if (!email) {
        throw new Error("Email is required to create your intake form.");
      }

      try {
        const payload: IntakeData = {
          ...intakeDataRef.current,
          email,
          photos: current.photos || {},
          additionalPhotos: current.additionalPhotos || [],
          xrays: current.xrays || [],
          consentGiven: current.consentGiven ?? false,
        };

        const response = await patientIntakeApi.submitIntake(payload);
        const formUid = response.data?.formUid;
        const referenceUid = response.data?.referenceUid;

        if (!formUid) {
          throw new Error("Failed to create intake form.");
        }

        updateIntakeData({ formUid, referenceUid });
        if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
          window.clearTimeout(draftSyncTimeoutRef.current);
          draftSyncTimeoutRef.current = null;
        }
        lastSyncedSignatureRef.current = JSON.stringify(
          buildFormPayload(intakeDataRef.current),
        );
        console.log("✅ Created new intake form draft:", formUid);
      } catch (creationError) {
        console.error("Error creating intake form:", creationError);
        const message =
          creationError instanceof Error
            ? creationError.message
            : "We could not create your intake form. Please try again.";
        throw new Error(message);
      }
    },
    [updateIntakeData],
  );

  const nextStep = async () => {
    console.log(
      "🚀 nextStep called, current step:",
      currentStep,
      "going to:",
      currentStep + 1,
    );
    if (currentStep < STEPS.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextStepName = STEPS[nextStepIndex]?.title || "Unknown";

      console.log(
        `📱 Transitioning from step ${currentStep} to step ${nextStepIndex} (${nextStepName})`,
      );

      // Create form when transitioning to review step (step 6) - after all data is collected
      if (nextStepIndex === 6 && !intakeDataRef.current.formUid) {
        console.log("📝 Creating intake form before review...");
        try {
          const response = await patientIntakeApi.submitIntake(
            intakeDataRef.current,
          );
          const formUid = response.data?.formUid;
          const referenceUid = response.data?.referenceUid;

          if (formUid) {
            updateIntakeData({ formUid, referenceUid });
            if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
              window.clearTimeout(draftSyncTimeoutRef.current);
              draftSyncTimeoutRef.current = null;
            }
            lastSyncedSignatureRef.current = JSON.stringify(
              buildFormPayload(intakeDataRef.current),
            );
            console.log("✅ Form created with UID:", formUid);
          } else {
            throw new Error("Failed to create intake form");
          }
        } catch (error) {
          console.error("Error creating intake form:", error);
          alert(
            "There was an error creating your intake form. Please try again.",
          );
          return; // Don't proceed to next step
        }
      }

      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant" as ScrollBehavior,
        });
      }

      setCurrentStep(currentStep + 1);

      setTimeout(() => {
        console.log(
          `✅ Step transition complete. Now on step ${nextStepIndex}: ${nextStepName}`,
        );
      }, 100);
    } else {
      console.log("⛔ Cannot proceed - already at last step");
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant" as ScrollBehavior,
        });
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRetakePhoto = (payload: {
    photoId: string;
    mediaId?: string;
  }) => {
    setRetakePhotoContext(payload);
  };

  const handleRetakeComplete = () => {
    setRetakePhotoContext(null);
    setCurrentStep(STEPS.findIndex((step) => step.id === "review"));
  };

  const handleRetakeCancel = () => {
    setRetakePhotoContext(null);
  };

  const handleOTPVerificationSuccess = () => {
    // Redirect to jema-manager will be handled in the OTP component
    nextStep(); // Go to success step
  };

  const submitIntake = async () => {
    setIsSubmitting(true);
    try {
      await flushDraftSync();

      const currentData = intakeDataRef.current;

      const cleanedPhotos: Record<string, string> = {};
      Object.entries(currentData.photos).forEach(([key, value]) => {
        if (
          value &&
          typeof value === "string" &&
          !value.startsWith("data:") &&
          !value.startsWith("blob:")
        ) {
          cleanedPhotos[key] = value;
        } else {
          console.warn(
            `Skipping ${key} photo: contains base64/blob data instead of URL`,
          );
        }
      });

      console.log(
        "Submitting intake with cleaned photo URLs:",
        Object.keys(cleanedPhotos),
      );

      // Form is already created, just proceed to OTP verification
      if (currentData.formUid) {
        console.log("✅ Finalizing intake form before verification", currentData.formUid);
        await patientIntakeApi.finalizeIntake(currentData.formUid);
        console.log("✅ Form already exists, proceeding to OTP verification");

        if (typeof window !== "undefined" && (window as any).gtag) {
          (window as any).gtag("event", "intake_submit_success", {
            event_category: "dental_intake",
            event_label: "intake_completed",
          });
        }
        nextStep(); // Go to OTP verification step
      } else {
        throw new Error("Form ID not found. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting intake:", error);
      alert("There was an error submitting your intake. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepData = STEPS[currentStep];

  if (retakePhotoContext) {
    return (
      <SinglePhotoRetake
        data={intakeData}
        photoId={retakePhotoContext.photoId}
        existingMediaId={retakePhotoContext.mediaId}
        photoTitle=""
        onUpdate={updateIntakeData}
        onComplete={handleRetakeComplete}
        onCancel={handleRetakeCancel}
      />
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <MemoizedWelcomeStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onEnsureDraft={ensureDraftForm}
            onNext={nextStep}
          />
        );
      case 1:
        return (
          <MemoizedMedicalIntakeStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <MemoizedDynamicQuestionTree
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <MemoizedPhotoCaptureStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
          />
        );
      case 4:
        return (
          <MemoizedAdditionalPhotosStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 5:
        return (
          <MemoizedComprehensiveDentalAnalysis
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
          />
        );
      case 6:
        return (
          <MemoizedReviewStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onSubmit={submitIntake}
            isSubmitting={isSubmitting}
            onRetakePhoto={handleRetakePhoto}
          />
        );
      case 7:
        return (
          <MemoizedOTPVerificationStep
            data={intakeData}
            onUpdate={updateIntakeData}
            onVerificationSuccess={handleOTPVerificationSuccess}
            onBack={prevStep}
            formUid={intakeData.formUid}
            referenceUid={intakeData.referenceUid}
          />
        );
      case 8:
        return <MemoizedSuccessStep />;
      default:
        return null;
    }
  };

  const stepContent = renderStep();

  return (
    <div className="min-h-screen bg-gray-50 md:bg-white">
      {isDesktop ? (
        <div className="flex h-screen bg-gray-50">
          <aside className="w-72 lg:w-80 flex-shrink-0 bg-gradient-to-br from-[#166B93] via-[#166B93] to-[#CEF12F] text-white flex flex-col shadow-lg">
            <div className="flex-shrink-0 p-6 border-b border-white/20">
              <div className="flex flex-col items-center">
                <img
                  src="/jema services logo full.svg"
                  alt="Jema Services Logo"
                  className=" object-contain brightness-0 invert"
                />
              </div>
              <div className="text-white">
                <h1 className="text-lg font-semibold text-white mb-1">
                  Dental Care Without Borders
                </h1>
                <h2 className="text-sm text-white/90 mb-4">
                  Premium Dental Tourism in Colombia
                </h2>
                <p className="text-xs text-white/80 leading-relaxed">
                  Travel to Colombia for world-class dental care powered by AI
                  technology. Personalized treatment plans, expert dentists, and
                  a new smile worth the journey.
                </p>
              </div>
            </div>
            {currentStep < STEPS.length - 1 ? (
              <nav className="flex-1 px-4 py-6 space-y-2 scroll-smooth overflow-hidden">
                <h3 className="text-white font-medium text-xs mb-3 px-4 uppercase tracking-wider">
                  Your Progress
                </h3>
                {STEPS.slice(0, -1).map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;
                  return (
                    <div
                      key={step.id}
                      ref={(el) => {
                        if (el) stepRefs.current[index] = el;
                      }}
                      className={`flex items-center gap-4 p-3 mx-2 rounded-lg transition-all duration-300 cursor-default ${isActive ? "bg-white/25 shadow-md" : isCompleted ? "text-white/80 bg-white/10 hover:bg-white/15" : "text-white/60"}`}
                    >
                      <div
                        className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${isActive ? "bg-white text-[#166B93]" : isCompleted ? "bg-[#CEF12F] text-gray-900" : "bg-white/20 text-white/70"}`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm lg:text-base truncate">
                          {step.title}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </nav>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-white mb-4" />
                  <p className="mt-4 text-lg">Thank you!</p>
                </div>
              </div>
            )}
            <div className="flex-shrink-0 p-4 border-t border-white/20 text-white/60 text-xs text-center">
              <p>© 2024 Jema Services. All Rights Reserved.</p>
            </div>
          </aside>
          <div className="flex-1 flex flex-col min-h-0">
            <header className="flex-shrink-0 bg-white border-b border-gray-200">
              <div className="h-20 flex items-center justify-between px-6 lg:px-8">
                {currentStep < STEPS.length - 1 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#166B93]/10 text-[#166B93]">
                      <currentStepData.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-lg">
                        {currentStepData.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Step {currentStep + 1} of {STEPS.length - 1}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {currentStepData.title}
                    </h3>
                  </div>
                )}

                {currentStep > 0 && currentStep < STEPS.length - 1 && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={prevStep}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </Button>
                )}
              </div>
            </header>
            <main className="flex-1 overflow-y-auto bg-white" ref={contentRef}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {stepContent}
              </div>
            </main>
          </div>
        </div>
      ) : (
        <div className="ios-viewport-fix">
          <ResponsiveHeader
            currentStep={currentStep}
            totalSteps={STEPS.length}
            onBack={
              currentStep > 0 && currentStep < STEPS.length - 1
                ? prevStep
                : undefined
            }
            showProgress={true}
            stepTitle={currentStepData.title}
            stepIcon={currentStepData.icon}
          />
          <div className="px-4 py-6">
            <Card className="shadow-lg border-0 w-full">
              <CardContent className="p-6 sm:p-8">{stepContent}</CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
